import "maplibre-gl/dist/maplibre-gl.css";

import { useSuspenseQuery } from "@apollo/client/react";
import { MapController, MapView, type PickingInfo, WebMercatorViewport } from "@deck.gl/core";
import { GeoJsonLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as maplibre from "react-map-gl/maplibre";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { PanelLoading } from "~/components/loading";
import { getCategoryColor } from "~/components/timeline";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import {
  GetRecordsForMapDocument,
  type GetRecordsForMapQuery,
  type GetRecordsForMapQueryVariables,
} from "~/features/entities/gqls";
import { useMapGeoRecords } from "~/features/entities/hooks/useMapGeoRecords";
import type { EntityListFilters, MapViewport } from "~/features/entities/types";
import {
  extractCharacterSet,
  type HoverInfo,
  isInYearRange,
  type MapPointGeoJSON,
  pointToGeoJSON,
  recordsToMapPoints,
} from "~/features/entities/utils/mapHelpers";
import { buildEntitiesWhere } from "~/features/entities/utils/transformers";
import { useMapDateRange } from "~/features/entities/utils/useMapDateRange";
import { useTenant } from "~/features/tenant/context";
import { mapStyle } from "~/lib/map/components/constants";
import { parseGdtfRange } from "~/utils/gdtfRange";
import { MapDateController } from "./MapDateController";

const MAP_HEIGHT = 600;
const MAX_MAP_RECORDS = 5000;
/** Safety ceiling: if more than this many points fall inside the viewport, skip labels (too cluttered to read). */
const MAX_LABELS = 1200;
const POINT_COLOR: [number, number, number, number] = [0, 0, 0, 200];
const POINT_COLOR_SELECTED: [number, number, number, number] = [255, 87, 34, 255];

const ZOOM_MIN = 1;
const ZOOM_MAX = 16;

interface MapViewState {
  latitude: number;
  longitude: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
  minZoom?: number;
  maxZoom?: number;
}

const DEFAULT_VIEW_STATE: MapViewState = {
  latitude: 35.0,
  longitude: 135.0,
  zoom: 4,
  pitch: 0,
  bearing: 0,
  minZoom: ZOOM_MIN,
  maxZoom: ZOOM_MAX,
};

const clampZoom = (zoom: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

interface EntitiesMapViewProps {
  filters: EntityListFilters;
  onSelect: (id: string) => void;
  /** Record whose map point is highlighted (the open peek selection). */
  selectedRecordId?: string;
  initialYearRange?: [number, number];
  onYearRangeChange?: (range: [number, number]) => void;
  initialViewport?: MapViewport;
  onViewportChange?: (viewport: MapViewport) => void;
}

export function EntitiesMapView({
  filters,
  onSelect,
  selectedRecordId,
  initialYearRange,
  onYearRangeChange,
  initialViewport,
  onViewportChange,
}: EntitiesMapViewProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const locale = useDisplayLocale();
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());
  const [hoverInfo, setHoverInfo] = useState<HoverInfo>(null);
  const [deckWidth, setDeckWidth] = useState(0);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const searchWhere = useMemo(() => ({ ...buildEntitiesWhere(filters), entityRecord: {} }), [filters]);
  const where = useMemo(
    () => ({ ...searchWhere, recordGeoItems: { deactivatedAt: { _isNull: true } } }),
    [searchWhere],
  );

  const { data } = useSuspenseQuery<GetRecordsForMapQuery, GetRecordsForMapQueryVariables>(GetRecordsForMapDocument, {
    variables: { tenantId, where, searchWhere, limit: MAX_MAP_RECORDS, offset: 0 },
  });

  const recordIds = useMemo(() => data.records.map(({ id }) => id), [data.records]);
  const { records: geoRecords, loading: geoRecordsLoading } = useMapGeoRecords(tenantId, recordIds);

  // Track the map's pixel width (drives viewport-based label culling).
  useEffect(() => {
    if (geoRecordsLoading) return;
    const el = mapContainerRef.current;
    if (!el) return;
    setDeckWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setDeckWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [geoRecordsLoading]);

  const allPoints = useMemo(
    () => recordsToMapPoints(data.records, geoRecords, locale),
    [data.records, geoRecords, locale],
  );

  // `yearBounds` is the absolute extent (real min/max — the hard drag limits).
  // `denseBounds` (2nd–98th percentile) is the initial window so the slider sticks to where the
  // data actually is rather than stretching to a single outlier year. The user can still drag out
  // to the true bounds (see the dynamic axis below).
  const { yearBounds, denseBounds } = useMemo(() => {
    const years = allPoints
      .flatMap((pt) => [pt.startYear, pt.endYear].filter((y): y is number => y !== null))
      .sort((a, b) => a - b);
    if (years.length === 0) return { yearBounds: null, denseBounds: null };
    const at = (p: number) => years[Math.min(years.length - 1, Math.max(0, Math.round((years.length - 1) * p)))];
    const dataMin = years[0];
    // Extend the upper bound to at least the current year so "now" is always selectable.
    const dataMax = Math.max(years[years.length - 1], new Date().getFullYear());
    // When a GDTF date filter is set, the slider must not extend beyond it.
    const gdtf = parseGdtfRange(filters.gdtfFrom, filters.gdtfTo);
    const min = gdtf.hasFilter && gdtf.fromYear != null ? gdtf.fromYear : dataMin;
    const max = gdtf.hasFilter && gdtf.toYear != null ? gdtf.toYear : dataMax;
    if (min >= max) return { yearBounds: null, denseBounds: null };
    // Initial window: the filter span when set, else the dense 2nd–98th percentile.
    const lo = Math.max(min, gdtf.hasFilter && gdtf.fromYear != null ? gdtf.fromYear : at(0.02));
    const hi = Math.min(max, gdtf.hasFilter && gdtf.toYear != null ? gdtf.toYear : at(0.98));
    return { yearBounds: { min, max }, denseBounds: { min: lo < hi ? lo : min, max: lo < hi ? hi : max } };
  }, [allPoints, filters.gdtfFrom, filters.gdtfTo]);

  const { yearRange, setYearRange } = useMapDateRange(
    yearBounds,
    initialYearRange ?? (denseBounds ? [denseBounds.min, denseBounds.max] : undefined),
  );

  // The slider axis isn't the full data extent — it's the selected window padded by its own span
  // (clamped to the true bounds). So handles stay usable, dragging out reveals more area, and the
  // axis dynamically shrinks as the window narrows. The true min/max remain the hard drag limits.
  const sliderAxis = useMemo(() => {
    if (!yearBounds || !yearRange) return null;
    const span = Math.max(50, yearRange[1] - yearRange[0]);
    return {
      min: Math.max(yearBounds.min, Math.floor(yearRange[0] - span)),
      max: Math.min(yearBounds.max, Math.ceil(yearRange[1] + span)),
    };
  }, [yearBounds, yearRange]);

  // Notify the parent (URL sync) when the year range changes, skipping the first render.
  const isFirstYearRangeRender = useRef(true);
  const onYearRangeChangeRef = useRef(onYearRangeChange);
  onYearRangeChangeRef.current = onYearRangeChange;
  useEffect(() => {
    if (!yearRange) return;
    if (isFirstYearRangeRender.current) {
      isFirstYearRangeRender.current = false;
      return;
    }
    onYearRangeChangeRef.current?.(yearRange);
  }, [yearRange]);

  // Controlled viewport so the zoom buttons can drive it. Seeded once from the URL.
  const [viewState, setViewState] = useState<MapViewState>(() =>
    initialViewport ? { ...DEFAULT_VIEW_STATE, ...initialViewport } : DEFAULT_VIEW_STATE,
  );
  const onViewStateChange = useCallback(({ viewState: next }: { viewState: Record<string, unknown> }) => {
    setViewState(next as unknown as MapViewState);
  }, []);
  const zoomBy = useCallback((delta: number) => {
    setViewState((v) => ({ ...v, zoom: clampZoom(v.zoom + delta) }));
  }, []);
  const resetView = useCallback(() => setViewState(DEFAULT_VIEW_STATE), []);

  // Debounced viewport → URL sync (covers drag/scroll and the zoom buttons), skipping first render.
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstViewportRender = useRef(true);
  useEffect(() => {
    if (isFirstViewportRender.current) {
      isFirstViewportRender.current = false;
      return;
    }
    if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current);
    viewportTimerRef.current = setTimeout(() => {
      onViewportChangeRef.current?.({
        latitude: Math.round(viewState.latitude * 10000) / 10000,
        longitude: Math.round(viewState.longitude * 10000) / 10000,
        zoom: Math.round(viewState.zoom * 10) / 10,
      });
    }, 400);
  }, [viewState]);

  const allCategories = useMemo(() => {
    const cats = new Map<string, string>();
    for (const pt of allPoints) {
      pt.categories.forEach((name, i) => {
        if (!cats.has(name)) cats.set(name, pt.categoryLabels[i] ?? name);
      });
    }
    return Array.from(cats, ([name, label]) => ({ name, label })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allPoints]);

  const filteredPoints = useMemo(
    () =>
      allPoints.filter((pt) => {
        if (pt.categories.length > 0 && pt.categories.every((c) => hiddenCategories.has(c))) return false;
        return isInYearRange(pt, yearRange);
      }),
    [allPoints, hiddenCategories, yearRange],
  );

  const features = useMemo(() => filteredPoints.map(pointToGeoJSON), [filteredPoints]);

  // Labels (a deck.gl TextLayer + CJK font atlas) are the dominant render cost, so they load
  // lazily: dots paint first, then labels appear on the next frames.
  const [labelsReady, setLabelsReady] = useState(false);
  useEffect(() => {
    setLabelsReady(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setLabelsReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [features]);

  // Debounced viewport so labels recompute after pan/zoom settles, not every frame.
  const [settledView, setSettledView] = useState(viewState);
  useEffect(() => {
    const id = setTimeout(() => setSettledView(viewState), 250);
    return () => clearTimeout(id);
  }, [viewState]);

  // Only label points inside the current viewport (culling) — keeps the TextLayer small even
  // with thousands of dots, and is why labels appear immediately for the few points on screen.
  const visibleFeatures = useMemo(() => {
    if (!labelsReady || deckWidth <= 0) return [];
    try {
      const vp = new WebMercatorViewport({
        width: deckWidth,
        height: MAP_HEIGHT,
        latitude: settledView.latitude,
        longitude: settledView.longitude,
        zoom: settledView.zoom,
      });
      // Project each point to screen pixels and keep those inside the canvas (version-robust vs. getBounds()).
      // An unnamed GeoItem has no label, so its dot stands alone and does not count toward MAX_LABELS.
      const inView = features.filter((f) => {
        if (!f.properties.label) return false;
        const [lng, lat] = f.geometry.coordinates;
        const [x, y] = vp.project([lng, lat]);
        return x >= 0 && x <= deckWidth && y >= 0 && y <= MAP_HEIGHT;
      });
      return inView.length > MAX_LABELS ? [] : inView;
    } catch {
      return [];
    }
  }, [labelsReady, deckWidth, settledView, features]);

  const showLabels = visibleFeatures.length > 0;
  const characterSet = useMemo(
    () => (showLabels ? extractCharacterSet(visibleFeatures) : []),
    [showLabels, visibleFeatures],
  );
  const totalCount = data.recordsAggregate.aggregate?.totalCount ?? 0;
  const showDateController = sliderAxis !== null && yearRange !== null && sliderAxis.min < sliderAxis.max;

  const handleClick = useCallback(
    (info: PickingInfo<MapPointGeoJSON>) => {
      if (info.object) onSelect(info.object.properties.recordId);
    },
    [onSelect],
  );

  const handleHover = useCallback((info: PickingInfo<MapPointGeoJSON>) => {
    setHoverInfo(info.object ? { x: info.x, y: info.y, object: info.object } : null);
  }, []);

  const toggleCategory = useCallback((category: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  const layers = useMemo(() => {
    // Dots — all points, cheap, painted immediately.
    const isSelected = (d: GeoJSON.Feature) => (d as MapPointGeoJSON).properties.recordId === selectedRecordId;
    const dots = new GeoJsonLayer({
      id: "v2-entities-dots",
      data: features,
      pickable: true,
      onClick: handleClick,
      onHover: handleHover,
      stroked: true,
      filled: true,
      pointType: "circle",
      getLineWidth: (d: GeoJSON.Feature) => (isSelected(d) ? 4 : 2),
      lineWidthUnits: "pixels" as const,
      getPointRadius: (d: GeoJSON.Feature) => (isSelected(d) ? 8 : 5),
      pointRadiusUnits: "pixels" as const,
      getFillColor: (d: GeoJSON.Feature) => (isSelected(d) ? POINT_COLOR_SELECTED : POINT_COLOR),
      getLineColor: (d: GeoJSON.Feature) => (isSelected(d) ? [255, 255, 255, 255] : [255, 255, 255, 200]),
      updateTriggers: {
        getFillColor: [selectedRecordId],
        getLineColor: [selectedRecordId],
        getPointRadius: [selectedRecordId],
        getLineWidth: [selectedRecordId],
      },
    });
    if (!showLabels) return [dots];
    // Labels — only the points inside the viewport, added once ready (lazy).
    const labels = new GeoJsonLayer({
      id: "v2-entities-labels",
      data: visibleFeatures,
      pickable: true,
      onClick: handleClick,
      onHover: handleHover,
      stroked: false,
      filled: false,
      pointType: "text",
      getText: (d: GeoJSON.Feature) => (d as MapPointGeoJSON).properties.label ?? null,
      getTextColor: [20, 20, 20],
      textFontFamily: "system-ui, sans-serif",
      getTextSize: 14,
      textSizeScale: 0.6,
      textFontWeight: 600,
      textBackground: true,
      textBackgroundPadding: [4, 2] as [number, number],
      getTextBackgroundColor: [255, 255, 255, 160],
      getTextPixelOffset: [0, -16] as [number, number],
      textCharacterSet: characterSet,
    });
    return [dots, labels];
  }, [features, visibleFeatures, characterSet, showLabels, handleClick, handleHover, selectedRecordId]);

  if (geoRecordsLoading) return <PanelLoading />;

  return (
    <div className="space-y-2">
      {totalCount > MAX_MAP_RECORDS && (
        <p className="rounded-[var(--radius-sm)] bg-bg-tint px-3 py-2 text-12-regular text-fg-2">
          {t("feature.map.truncatedWarning", { count: MAX_MAP_RECORDS })}
        </p>
      )}
      {filteredPoints.length === 0 && (
        <p className="rounded-[var(--radius-sm)] bg-bg-tint px-3 py-2 text-12-regular text-fg-2">
          {totalCount > 0 ? t("feature.map.noGeoItemsWithCount", { count: totalCount }) : t("feature.map.noGeoItems")}
        </p>
      )}

      <div className="overflow-hidden rounded-[var(--radius-md)] border border-border-default">
        <div ref={mapContainerRef} className="relative w-full" style={{ height: MAP_HEIGHT }}>
          <DeckGL
            id="v2-entities-map"
            viewState={viewState}
            layers={layers}
            views={new MapView({ repeat: true })}
            controller={{ type: MapController, dragRotate: false }}
            width="100%"
            height={MAP_HEIGHT}
            getCursor={({ isHovering }) => (isHovering ? "pointer" : "grab")}
            onViewStateChange={onViewStateChange}
          >
            <maplibre.Map key="v2-entities-map" mapStyle={mapStyle} />
          </DeckGL>

          {yearRange && (
            <div className="absolute left-12 top-3 z-10 text-[18px] font-bold leading-tight text-fg-1">
              {yearRange[0]} — {yearRange[1]}
            </div>
          )}

          <div className="absolute right-3 top-3 z-10 flex flex-col items-center gap-1 rounded-[var(--radius-sm)] bg-white/90 p-1 shadow-sm">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => zoomBy(1)}
              disabled={viewState.zoom >= ZOOM_MAX}
              aria-label={t("ui.zoomIn")}
            >
              <Plus />
            </Button>
            <span className="text-12-bold tabular-nums text-fg-2" title={t("ui.zoom", { defaultValue: "Zoom" })}>
              {Math.round(viewState.zoom)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => zoomBy(-1)}
              disabled={viewState.zoom <= ZOOM_MIN}
              aria-label={t("ui.zoomOut")}
            >
              <Minus />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={resetView}
              aria-label={t("ui.resetZoom")}
            >
              <RotateCcw />
            </Button>
          </div>

          {allCategories.length > 0 && (
            <div className="absolute left-2.5 top-14 z-10 flex max-h-[60%] flex-col gap-1 overflow-y-auto">
              {allCategories.map(({ name, label }) => {
                const hidden = hiddenCategories.has(name);
                const shortLabel = (label.split(".").pop() ?? label).slice(0, 2).toUpperCase();
                return (
                  <Tooltip key={name}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => toggleCategory(name)}
                        aria-label={label}
                        aria-pressed={!hidden}
                        className="flex h-7 w-7 items-center justify-center rounded-full border-2 text-[0.55rem] font-bold text-fg-1 transition-opacity hover:opacity-80"
                        style={{
                          backgroundColor: hidden ? "#d1d5db" : getCategoryColor([name]),
                          borderColor: hidden ? "#9ca3af" : "#4b5563",
                          opacity: hidden ? 0.4 : 1,
                        }}
                      >
                        {shortLabel}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}

          <div className="absolute bottom-2 left-3 z-10 rounded-[var(--radius-sm)] bg-white/85 px-2 py-0.5 text-12-regular text-fg-2">
            {t("feature.map.locationCount", { shown: filteredPoints.length, total: allPoints.length })}
          </div>

          {hoverInfo?.object && (
            <div
              className="pointer-events-none absolute z-20 max-w-[280px] rounded-[var(--radius-sm)] border border-border-default bg-white/95 px-3 py-2 shadow-md"
              style={{ left: hoverInfo.x + 12, top: hoverInfo.y - 12 }}
            >
              <p className="truncate text-[13px] font-semibold text-fg-1">{hoverInfo.object.properties.recordName}</p>
              {hoverInfo.object.properties.startYear !== null && (
                <p className="text-12-regular text-fg-3">
                  {hoverInfo.object.properties.startYear}
                  {hoverInfo.object.properties.endYear !== null &&
                  hoverInfo.object.properties.endYear !== hoverInfo.object.properties.startYear
                    ? ` — ${hoverInfo.object.properties.endYear}`
                    : ""}
                </p>
              )}
              {hoverInfo.object.properties.categories.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {hoverInfo.object.properties.categories.map((c, i) => (
                    <span
                      key={c}
                      className="rounded-[var(--radius-pill)] px-1.5 py-0.5 text-[0.6rem] font-semibold text-fg-1"
                      style={{ backgroundColor: getCategoryColor([c]) }}
                    >
                      {hoverInfo.object?.properties.categoryLabels[i] ?? c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {showDateController && sliderAxis && yearRange && (
          <div className="border-t border-border-default bg-white">
            <MapDateController min={sliderAxis.min} max={sliderAxis.max} value={yearRange} onChange={setYearRange} />
          </div>
        )}
      </div>
    </div>
  );
}
